/**
 * „Was als Nächstes laufen sollte" (Empfehlung, Plan 2026-10-14-webinar-empfehlung; Umbau P1-V3 § 2.2, 25.10.2026).
 * Kopf: was am Zielsonntag angelegt ist, was die Montags-Mail empfahl — Euro-Werte nur, wenn Sonntag und Rechenverfahren
 * gleich sind (K7) —, und für welchen Sonntag die Tabelle gilt (E5: nächster freier Sonntag, nur Oberfläche). Darunter die
 * Kandidaten mit Spaltenwahl, die Gesperrten als offener Abschnitt am Ende (Regel 14), ganz neue Webinare je Produkt,
 * „Hat die Empfehlung gestimmt?" (Bilanz ab 01.11., ohne vorab belegte Sonntage, K8) und die Pflege von Hand (zu, mit
 * Kopfzeile, E6-A). Rechnung: node-app/src/helpers/webinarPrognose.ts.
 */
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, kompakt, nachGruppenReihenfolge, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import { mitDeckkraft, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import {
  ABWEICHUNG_GRUENDE,
  EmpfehlungKopf,
  NachverfolgungAntwort,
  PrognoseErgebnis,
  PrognoseGrund,
  PrognoseKandidat,
  RankingAntwort,
  THEMEN,
  fetchNachverfolgung,
  fetchWebinarRanking,
  fetchWebinarRankingGeteilt,
  hebeVorschlagSperreAuf,
  holeKiThemen,
  legeAnlassAn,
  loescheAnlass,
  setzeAbweichung,
  setzeThema,
  setzeVorschlagSperre,
} from '../../../helpers/auswertungenData';
import { aehnlicherLief, chanceBesser, empfehlungBefund, gesperrtKopf, gleichaufBis, gruendeMitRichtung, type PausenBezug } from './empfehlungText';
import { breiteAus } from '../../../components/auswertungen/spaltenwahl';
import { useBreitePx } from '../../../components/auswertungen/kennzahlTeile';
import type { ZellKontext } from '../../../components/auswertungen/SortableTable';

/** Zellkontext für zusammengelegte Zellen (keine Sortierung, Ebene 0). */
const KONTEXT: ZellKontext = { sortKey: null, sortGrund: null, ebene: 0 };
import { datum, Hinweis, int, KlappKopf, Laden, NamensEtikett, offenBeiTermin, tagMitWochentag, tagMonat, Unter, ZuordnungLaeuft } from './shared';


const GRUND: Record<PrognoseGrund, string> = {
  kein_produkt: 'ohne Produkt',
  produkt_zu_frisch: 'Produkt lief kürzlich',
  konzept_zu_frisch: 'dieses Webinar lief kürzlich',
  thema_zu_frisch: 'Thema lief kürzlich',
  nicht_vorschlagen: 'von Hand gesperrt',
  produkt_gesperrt: 'Produkt gesperrt',
};
const THEMA_LABEL = new Map(THEMEN.map((t) => [t.key, t.label]));

/** Prognose in T€ wie die Spalte daneben (Leser V1). */
const tw = (v: number | null | undefined) => (v === null || v === undefined ? '–' : tEuro(v));
const spanneT = (k: { p25: number | null; p75: number | null }) =>
  k.p25 === null || k.p75 === null ? '–' : `${((k.p25 ?? 0) / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}–${tw(k.p75)}`;
const wochen = (t: number | null) => (t === null ? '–' : `${Math.round(t / 7)}`);
const plusTage = (d: string, n: number) => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

/**
 * Mittlere Hälfte als Balken, Median als Strich — eine Skala für die ganze Tabelle. Audit D11 (30.10.2026): Spur
 * mindestens 180 px breit (am Handy die Zellbreite), Interquartilsabstand in `series1Soft`, Median in `series1` (Platz 1–3)
 * bzw. `muted` (übrige, Kontext), Abstand zur Beschriftung; Farben aus `useVizStyle` statt fester Hex-Werte.
 */
const SpannenBalken: FC<{ k: { median: number | null; p25: number | null; p75: number | null }; max: number; aktiv: boolean }> = ({ k, max, aktiv }) => {
  const viz = useVizStyle();
  if (k.median === null || k.p25 === null || k.p75 === null || max <= 0) return <span>–</span>;
  const pos = (v: number) => `${Math.max(0, Math.min(100, (100 * v) / max))}%`;
  return (
    <Box sx={{ minWidth: { xs: 96, sm: 180 }, maxWidth: 260 }} data-grafik="spanne">
      <Box sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>≈ {tw(k.median)}</Box>
      <Box sx={{ position: 'relative', height: 8, mt: 0.75, mb: 0.75, bgcolor: viz.spur, borderRadius: 4 }}>
        <Box
          sx={{
            position: 'absolute',
            left: pos(k.p25),
            width: `calc(${pos(k.p75)} - ${pos(k.p25)})`,
            top: 0,
            bottom: 0,
            bgcolor: aktiv ? viz.series1Soft : mitDeckkraft(viz.muted, 0.35),
            borderRadius: 4,
          }}
        />
        <Box sx={{ position: 'absolute', left: pos(k.median), top: -3, bottom: -3, width: 3, ml: '-1.5px', bgcolor: aktiv ? viz.series1 : viz.muted, borderRadius: 1 }} />
      </Box>
      <Unter>
        mittlere Hälfte <span style={{ whiteSpace: 'nowrap' }}>{spanneT(k)}</span>
      </Unter>
    </Box>
  );
};

/** Titel mit Produkt und Zusatz (Themenliste der Pflege). */
const Titel: FC<{ title: string; produkt: string | null; zusatz?: string }> = ({ title, produkt, zusatz }) => (
  <Box sx={{ lineHeight: 1.3, maxWidth: 340 }}>
    <Box component="span" sx={{ fontWeight: 600 }}>
      {title}
    </Box>
    <Unter>
      {produkt ?? '–'}
      {zusatz ? ` · ${zusatz}` : ''}
    </Unter>
  </Box>
);

/**
 * Kopf (blauer Kasten): neutral, ohne Vergleich über verschiedene Rechenverfahren oder Sonntage (P1-V3 § 2.2).
 * Persona-Umbau B3 (28.10.2026): zwei Zeilen — was am Zielsonntag angelegt ist bzw. was die Montags-Mail empfahl, und die
 * Bilanz. „Die Tabelle gilt dem nächsten freien Sonntag" und „gleichauf" stehen jetzt in der Befundzeile darüber. Hinweise
 * (Thema fehlt, Unvollständig) bleiben als eigene Zeile, nur wenn sie zutreffen.
 */
const Kopf: FC<{ data: RankingAntwort; nv: NachverfolgungAntwort | null }> = ({ data, nv }) => {
  const e: EmpfehlungKopf | null = data.empfehlung;
  const p = data.prognose;
  if (!e || !p) return null;
  const ziel = e.ziel_datum;
  const angelegt = p.geplant;
  const top = e.kandidaten.filter((k) => k.klasse === 'top1' || k.klasse === 'top2' || k.klasse === 'top3').sort((a, b) => (a.rang ?? 9) - (b.rang ?? 9));
  const erster = top[0] ?? null;
  const vergleichbar = (e.wertbasis ?? 'bestellung') === 'bestellung';
  const quelle = e.quelle === 'festgeschrieben' ? `die Montags-Mail vom ${tagMonat(e.erstellt_am)}` : 'die Vorschau (wird Montag 09:00 festgeschrieben)';
  const Quelle = quelle.charAt(0).toUpperCase() + quelle.slice(1);
  const bilanz = nv?.bilanz;
  const ab = bilanz?.ab ?? '2026-11-01';
  const letzter = data.rows[0] ?? null;
  const letzterKand = letzter ? p.kandidaten.find((k) => k.runs.some((r) => r.webinar_id === letzter.webinar_id)) : undefined;
  const letzterOhneThema = letzter && letzterKand && !letzterKand.thema ? letzter : null;
  return (
    <Alert severity="info" icon={false} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%', lineHeight: 1.6 } }}>
      <Box>
        {angelegt.length > 0 ? (
          <>
            <b>
              {tagMitWochentag(ziel)}: angelegt ist {angelegt.map((g) => `${g.title} (${g.plx_product ?? '–'})`).join(', ')}.
            </b>{' '}
            {erster && !angelegt.some((g) => g.key === erster.key) && (
              <>
                {Quelle} hatte <i>{erster.title}</i> ({erster.plx_product}) vorn.{' '}
              </>
            )}
            {e.quelle === 'festgeschrieben' && e.geplant.length > 0 && 'Der Sonntag war bei der Empfehlung schon belegt. '}
            {!vergleichbar && 'Ihre Euro-Werte sind mit den heutigen nicht vergleichbar (anderes Rechenverfahren bis 19.10.) und stehen deshalb hier nicht.'}
          </>
        ) : (
          <>
            <b>Für {tagMitWochentag(ziel)} ist noch kein Webinar angelegt.</b>{' '}
            {top.length > 0 ? (
              <>
                {Quelle} empfiehlt (netto):{' '}
                {top.map((k, i) => (
                  <Fragment key={k.key}>
                    {i > 0 && ' · '}
                    {k.rang}. <b>{k.title}</b> ({k.plx_product}){vergleichbar && ` ≈ ${tw(k.median)}`}
                    {k.anlass && <Chip size="small" label={`Anlass: ${k.anlass.titel}`} sx={{ ml: 1, bgcolor: '#fff6dc' }} />}
                  </Fragment>
                ))}
              </>
            ) : (
              'Kein Webinar frei — alle Kandidaten sind gesperrt.'
            )}
          </>
        )}
      </Box>
      <Box sx={{ mt: 0.5 }}>
        <b>{TERMS.bilanzEmpfehlung.label}</b>{' '}
        {bilanz && (bilanz.beurteilbar > 0 || (bilanz.freie_sonntage ?? 0) > 0) ? (
          <>
            In der mittleren Hälfte gelandet: {int(bilanz.in_spanne)} von {int(bilanz.beurteilbar)} Sonntagen · Empfehlung gelaufen: {int(bilanz.gelaufen ?? 0)} von{' '}
            {int(bilanz.freie_sonntage ?? 0)} freien Sonntagen.
          </>
        ) : (
          <>
            Gezählt wird ab dem {tagMonat(ab)}, davor galt ein anderes Rechenverfahren. Die erste Bilanz kommt ab {tagMonat(plusTage(ab, 14))} (14 Tage Nachlauf).
          </>
        )}
        <Tooltip title={TERMS.bilanzEmpfehlung.tip} arrow>
          <span style={{ cursor: 'help' }}> ⓘ</span>
        </Tooltip>
      </Box>
      {letzterOhneThema && (
        // B25: ohne Thema des letzten Termins greift die Themen-Sperre nicht — ein ähnliches Thema stünde sonst unmarkiert vorn.
        <Box sx={{ mt: 0.5, color: '#8a6100' }}>
          Thema von {tagMitWochentag(letzterOhneThema.start_date)} ({letzterOhneThema.public_title}) fehlt — die Sperre „Thema lief kürzlich“ greift dafür nicht. Unter
          „{TERMS.vonHandPflegen.label}“ nachtragen.
        </Box>
      )}
      {data.zusatz_fehler.length > 0 && <Box sx={{ mt: 0.5, color: '#8a6100' }}>Unvollständig: {data.zusatz_fehler.join(', ')} nicht geladen — Sperren/Themen können fehlen.</Box>}
    </Alert>
  );
};

/** Befundzeile (B3): die Antwort der Karte groß, aus `empfehlungBefund` (empfehlungText.ts). */
const Befund: FC<{ data: RankingAntwort }> = ({ data }) => {
  const p = data.prognose_frei ?? data.prognose;
  if (!p) return null;
  const namen = new Map(data.rows.filter((r) => r.plx_product).map((r) => [r.plx_product as string, r.product_name]));
  return (
    <BefundZeile
      text={
        <Box component="span" sx={{ fontSize: 16, fontWeight: 600 }}>
          {empfehlungBefund(p, (c) => (/^\d/.test(c) ? (namen.get(c) ?? c) : c))}
        </Box>
      }
      tip={`Platz 1 der Tabelle unten, für den nächsten freien Sonntag. ${TERMS.gleichauf.label}: ${TERMS.gleichauf.tip}`}
    />
  );
};

/** Thema setzen (manuell gewinnt immer vor der KI). */
const ThemaWahl: FC<{ k: PrognoseKandidat; onSaved: () => void }> = ({ k, onSaved }) => {
  const [wert, setWert] = useState(k.thema ?? '');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => setWert(k.thema ?? ''), [k.thema]);
  const speichern = async (neu: string) => {
    setWert(neu);
    setBusy(true);
    setFehler(null);
    try {
      await setzeThema(k.last_webinar_id, neu);
      onSaved();
    } catch (e) {
      setFehler((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ minWidth: 150 }}>
      <TextField
        select
        size="small"
        value={wert}
        disabled={busy}
        onChange={(ev) => speichern(ev.target.value)}
        SelectProps={{ displayEmpty: true }}
        sx={{ minWidth: 150 }}
      >
        <MenuItem value="" disabled>
          ohne Thema
        </MenuItem>
        {THEMEN.map((t) => (
          <MenuItem key={t.key} value={t.key}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>
      {k.thema_quelle === 'ki' && (
        <Box sx={{ fontSize: 12, color: '#8a6100', mt: 0.25 }}>
          KI-Vorschlag —{' '}
          <Link
            component="button"
            variant="caption"
            onClick={() => speichern(k.thema as string)}
            disabled={busy}
          >
            bestätigen
          </Link>
        </Box>
      )}
      {fehler && <Box sx={{ fontSize: 12, color: 'error.main' }}>{fehler}</Box>}
    </Box>
  );
};

const SperrenBereich: FC<{ data: RankingAntwort; onSaved: () => void }> = ({ data, onSaved }) => {
  const [produkt, setProdukt] = useState('');
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const produkte = useMemo(
    () =>
      [
        ...new Set(
          (data.prognose?.kandidaten ?? [])
            .map((k) => k.plx_product)
            .filter((p): p is string => !!p),
        ),
      ].sort(),
    [data],
  );
  const titelVon = useMemo(
    () =>
      new Map(
        (data.prognose?.kandidaten ?? []).flatMap((k) =>
          k.runs.map((r) => [r.webinar_id, `${k.title} (${k.plx_product})`] as const),
        ),
      ),
    [data],
  );
  const lauf = async (fn: () => Promise<unknown>) => {
    setFehler(null);
    try {
      await fn();
      onSaved();
    } catch (e) {
      setFehler((e as Error).message);
    }
  };
  return (
    <Box>
      {data.sperren.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Keine Sperren von Hand.
        </Typography>
      )}
      {data.sperren.map((s) => (
        <Box
          key={s.id}
          sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
        >
          <Chip
            size="small"
            label={
              s.art === 'produkt'
                ? `Produkt ${s.schluessel}`
                : (titelVon.get(s.schluessel) ?? `Webinar ${s.schluessel}`)
            }
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {s.grund ?? 'ohne Grund'} · {s.erstellt_von ?? '–'}, {datum(s.erstellt_am)}
          </Typography>
          <Button
            size="small"
            onClick={() => lauf(() => hebeVorschlagSperreAuf(s.art, s.schluessel))}
          >
            aufheben
          </Button>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
        <TextField
          select
          size="small"
          label="Produkt sperren"
          value={produkt}
          onChange={(e) => setProdukt(e.target.value)}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">—</MenuItem>
          {produkte.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Grund"
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          sx={{ minWidth: 220 }}
          placeholder="z. B. nicht mehr verkäuflich"
        />
        <Button
          size="small"
          variant="outlined"
          disabled={!produkt}
          onClick={() =>
            lauf(() =>
              setzeVorschlagSperre('produkt', produkt, grund || null).then(() => {
                setProdukt('');
                setGrund('');
              }),
            )
          }
        >
          sperren
        </Button>
      </Box>
      {fehler && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {fehler}
        </Alert>
      )}
      <Hinweis>
        Ein einzelnes Webinar sperrst du in der Tabelle oben („nicht vorschlagen“). Die Sperre gilt
        für die jetzige Aufzeichnung; ein neuer Lauf des Webinars hebt sie auf.
      </Hinweis>
    </Box>
  );
};

const AnlaesseBereich: FC<{ data: RankingAntwort; onSaved: () => void }> = ({ data, onSaved }) => {
  const [neu, setNeu] = useState({ datum: '', titel: '', thema: '' });
  const [fehler, setFehler] = useState<string | null>(null);
  const lauf = async (fn: () => Promise<unknown>) => {
    setFehler(null);
    try {
      await fn();
      onSaved();
    } catch (e) {
      setFehler((e as Error).message);
    }
  };
  return (
    <Box>
      {data.anlaesse.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Keine Anlässe im Zeitraum.
        </Typography>
      )}
      {data.anlaesse.map((a) => (
        <Box
          key={a.id}
          sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
        >
          <Typography variant="body2" sx={{ minWidth: 90 }}>
            {datum(a.datum)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {a.titel}
          </Typography>
          {a.thema && <Chip size="small" label={THEMA_LABEL.get(a.thema) ?? a.thema} />}
          <Button size="small" onClick={() => lauf(() => loescheAnlass(a.id))}>
            löschen
          </Button>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
        <TextField
          size="small"
          type="date"
          label="Datum"
          value={neu.datum}
          onChange={(e) => setNeu({ ...neu, datum: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Anlass"
          value={neu.titel}
          onChange={(e) => setNeu({ ...neu, titel: e.target.value })}
          sx={{ minWidth: 240 }}
          placeholder="z. B. NVIDIA Quartalszahlen"
          inputProps={{ maxLength: 120 }}
        />
        <TextField
          select
          size="small"
          label="Thema"
          value={neu.thema}
          onChange={(e) => setNeu({ ...neu, thema: e.target.value })}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">ohne Thema</MenuItem>
          {THEMEN.map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="outlined"
          disabled={!neu.datum || !neu.titel.trim()}
          onClick={() =>
            lauf(() =>
              legeAnlassAn({
                datum: neu.datum,
                titel: neu.titel.trim(),
                thema: neu.thema || null,
                notiz: null,
              }).then(() => setNeu({ datum: '', titel: '', thema: '' })),
            )
          }
        >
          anlegen
        </Button>
      </Box>
      {fehler && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {fehler}
        </Alert>
      )}
      <Hinweis>
        Ein Anlass mit Thema erscheint als Hinweis bei Kandidaten desselben Themas, wenn er bis 3
        Tage vor oder 7 Tage nach dem Sonntag liegt. Er ändert keinen Wert.
      </Hinweis>
    </Box>
  );
};

const AbweichungWahl: FC<{ z: NachverfolgungAntwort['zeilen'][number]; onSaved: () => void }> = ({
  z,
  onSaved,
}) => {
  const [grund, setGrund] = useState(z.abweichung_grund ?? '');
  const [notiz, setNotiz] = useState(z.abweichung_notiz ?? '');
  const [status, setStatus] = useState<string | null>(null);
  const speichern = async () => {
    setStatus(null);
    try {
      await setzeAbweichung(z.ziel_datum, grund, notiz || null);
      setStatus('gespeichert');
      onSaved();
    } catch (e) {
      setStatus((e as Error).message);
    }
  };
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField
        select
        size="small"
        value={grund}
        onChange={(e) => setGrund(e.target.value)}
        sx={{ minWidth: 0, width: 130 }}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">Grund wählen</MenuItem>
        {ABWEICHUNG_GRUENDE.map((g) => (
          <MenuItem key={g.key} value={g.key}>
            {g.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
        placeholder="Notiz"
        sx={{ minWidth: 0, width: 120 }}
        inputProps={{ maxLength: 255 }}
      />
      <Button size="small" disabled={!grund} onClick={speichern}>
        speichern
      </Button>
      {status && (
        <Typography
          variant="caption"
          sx={{ color: status === 'gespeichert' ? 'success.main' : 'error.main' }}
        >
          {status}
        </Typography>
      )}
    </Box>
  );
};

/** „Hat die Empfehlung gestimmt?" — Tabelle, offen (Regel 14). */
const Nachverfolgung: FC<{ data: NachverfolgungAntwort | null; fehler: string | null; onSaved: () => void; zuordnung?: RankingAntwort['zuordnung'] }> = ({
  data,
  fehler,
  onSaved,
  zuordnung,
}) => {
  // Schmal (< 640 px) stehen „Gelaufen" und der Grund unter dem Sonntag — zwei Spalten statt drei.
  const [breiteRef, breitePx] = useBreitePx();
  const schmal = breitePx !== null && breiteAus(breitePx) === 'schmal';
  if (!data) return <Laden fehler={fehler} />;
  const ab = data.bilanz.ab ?? '2026-11-01';
  if (!data.zeilen.length) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>Noch keine festgeschriebene Empfehlung. Erste Bilanz ab {tagMonat(plusTage(ab, 14))}.</Typography>;
  type Z = NachverfolgungAntwort['zeilen'][number];
  // Vier Spalten, Werte gestapelt (keine Spaltenwahl hier): sieben Spalten liefen bei 654 px über (Abnahme 25.10.).
  const spanneSatz = (z: Z): string => {
    if (z.wertbasis === 'alt') return 'anderes Rechenverfahren, zählt nicht';
    if (z.geplant_bei_empfehlung) return 'bei der Empfehlung schon belegt';
    const a = z.abgleich.find((x) => x.in_spanne !== null);
    return a ? (a.in_spanne ? 'in der mittleren Hälfte' : 'außerhalb der mittleren Hälfte') : '';
  };
  const spalten: Spalte<Z>[] = [
    {
      key: 'sonntag',
      label: 'Sonntag',
      render: (z) => {
        const e1 = z.empfohlen.find((k) => k.rang === 1);
        return (
          <Box sx={{ lineHeight: 1.3 }}>
            {tagMitWochentag(z.ziel_datum)}
            <Unter>Empfohlen: {e1 ? `${e1.title} (${e1.plx_product})` : '–'}</Unter>
          </Box>
        );
      },
      sortValue: (z) => z.ziel_datum,
    },
    {
      key: 'gelaufen',
      label: 'Gelaufen',
      // Der Grund einer Abweichung steht unter dem Gelaufenen (eigene Spalte lief bei 267 px über).
      render: (z) => (
        <Box sx={{ lineHeight: 1.3 }}>
          {z.stand === 'offen' ? (
            <Box component="span" sx={{ color: 'text.secondary' }}>noch nicht gelaufen</Box>
          ) : z.stand === 'kein_webinar' ? (
            'kein Webinar'
          ) : (
            z.abgleich.map((a) => `${a.title} (${a.plx_product ?? '–'})${a.zustand === 'befolgt' && a.rang === 1 ? ' — die Empfehlung' : ''}`).join(' · ')
          )}
          {z.stand !== 'offen' && (z.abgleich.some((a) => a.zustand !== 'befolgt') || z.stand === 'kein_webinar' || z.abweichung_grund) && (
            <Box sx={{ mt: 0.5 }}>
              <AbweichungWahl z={z} onSaved={onSaved} />
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'ergebnis',
      label: 'Ergebnis',
      einheit: `${TERMS.prognoseTermin.einheit} · Prognose`,
      align: 'right',
      render: (z) => {
        const a = z.abgleich[0];
        if (!a) return '–';
        return (
          <Box sx={{ lineHeight: 1.25, color: a.reif ? 'inherit' : 'text.disabled' }}>
            {tw(a.wert)}
            <Unter>
              {a.p25 !== null && a.p75 !== null ? `Prognose ${spanneT(a)}` : ''}
              {!a.reif ? ' · Zahlen noch offen' : ''}
            </Unter>
            <Unter>{spanneSatz(z)}</Unter>
            <ZuordnungLaeuft n={z.abgleich.reduce((s, x) => s + offenBeiTermin(zuordnung, x.webinar_id), 0)} lauf={zuordnung?.lauf} />
          </Box>
        );
      },
    },
  ];
  const sichtbar = schmal
    ? [{ ...spalten[0], render: (z: Z) => <Box>{spalten[0].render(z, KONTEXT)}<Box sx={{ mt: 0.5, fontSize: 13 }}>{spalten[1].render(z, KONTEXT)}</Box></Box> }, spalten[2]]
    : spalten;
  return (
    <Box ref={breiteRef}>
      <SortableTable rows={data.zeilen} columns={sichtbar} rowKey={(z) => z.ziel_datum} initialSort={{ key: 'sonntag', dir: 'desc' }} maxHeight={null} eng />
    </Box>
  );
};

/** Menü ⋮ „nicht vorschlagen" mit Rückfrage (schrieb früher sofort eine Sperre, P1-V3 § 2.2). */
const NichtVorschlagen: FC<{ k: PrognoseKandidat; onSaved: () => void }> = ({ k, onSaved }) => {
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const [frage, setFrage] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const sperren = async () => {
    setFehler(null);
    try {
      await setzeVorschlagSperre('gruppe', k.runs[0]?.webinar_id ?? k.last_webinar_id, 'nicht vorschlagen');
      setFrage(false);
      onSaved();
    } catch (e) {
      setFehler((e as Error).message);
    }
  };
  if (k.gruende.includes('nicht_vorschlagen')) return null;
  return (
    <>
      <IconButton size="small" aria-label={`Aktionen für ${k.title}`} onClick={(e) => setAnker(e.currentTarget)} sx={{ p: 0.25, ml: 0.5 }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anker} open={anker !== null} onClose={() => setAnker(null)}>
        <MenuItem
          onClick={() => {
            setAnker(null);
            setFrage(true);
          }}
        >
          nicht mehr vorschlagen …
        </MenuItem>
      </Menu>
      <Dialog open={frage} onClose={() => setFrage(false)}>
        <DialogTitle>„{k.title}“ nicht mehr vorschlagen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Das Webinar verschwindet aus den Kandidaten (z. B. weil die Aufzeichnung veraltet ist). Ein neuer Termin des Webinars hebt die Sperre auf; unter
            „Von Hand pflegen → Sperren“ lässt sie sich zurücknehmen.
          </Typography>
          {fehler && <Alert severity="error" sx={{ mt: 1 }}>{fehler}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFrage(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={sperren}>
            Nicht mehr vorschlagen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const KAND_GRUPPEN = ['Wert', 'Planung', 'Zahlung (wächst mit dem Alter)'] as const;
/** Ab diesem Platz (exklusiv) stehen Kandidaten als kompakter Einzeiler (Persona-Umbau B3). */
const KOMPAKT_AB = 10;
/** D14 (Audit 30.10.2026): EIN Titelstil für alle Plätze — vorher stand Platz 11 ff. größer als Platz 1–10. */
const TITEL_SX = { fontWeight: 600, fontSize: 13 } as const;
const KAND_STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['platz', 'titel', 'prognose', 'letzte', 'warum'],
  mittel: ['platz', 'titel', 'prognose', 'warum'],
  schmal: ['titel', 'prognose'],
};

const KandidatenTabelle: FC<{ data: RankingAntwort; onSaved: () => void }> = ({ data, onSaved }) => {
  const p: PrognoseErgebnis | null = data.prognose_frei ?? data.prognose;
  // Kontakte und Erwarteter Wert ohne vorläufige je Lauf — aus der Webinar-Tabelle derselben Antwort (Fallback).
  const lauf = useMemo(() => new Map(data.rows.map((r) => [r.webinar_id, r])), [data]);
  const produktName = useMemo(() => new Map(data.rows.filter((r) => r.plx_product).map((r) => [r.plx_product as string, r.product_name])), [data]);
  const erster = p?.kandidaten.find((k) => k.rang === 1) ?? null;
  const max = Math.max(...(p?.kandidaten ?? []).map((k) => k.p75 ?? 0), 1);
  const n = p ? gleichaufBis(p.kandidaten) : 1;
  const schwelle = p ? (p.kandidaten.find((k) => k.rang === n) ?? null) : null;
  const gesperrt = (p?.kandidaten ?? []).filter((k) => k.rang === null);
  const abschnitt = p ? gesperrtKopf(p.zieltermin, gesperrt, schwelle ? { platz: n, median: schwelle.median } : null) : '';
  const alleZeilen = useMemo(
    () => [...(p?.kandidaten ?? [])].sort((a, b) => (a.rang ?? 1e9) - (b.rang ?? 1e9) || (a.frei_ab ?? '').localeCompare(b.frei_ab ?? '') || (b.median ?? 0) - (a.median ?? 0)),
    [p],
  );
  // Gesperrte bleiben offen (P1-V3 Regel 14, E6 A) — B33 „nur die über Platz 3" ist damit abgelehnt.
  const zeilen = alleZeilen;
  // B24: ähnlicher Titel desselben Produkts lief jünger (anderes Konzept) — als Minuspunkt, Inhalt beurteilt Marketing.
  const aehnlich = useMemo(() => new Map((p?.kandidaten ?? []).map((k) => [k.key, aehnlicherLief(k, p?.kandidaten ?? [])])), [p]);
  const bezug = (k: PrognoseKandidat): PausenBezug | undefined =>
    p ? { ziel: p.zieltermin, pause_produkt: k.pause_produkt, pause_konzept: k.pause_konzept, pause_thema: k.pause_thema } : undefined;
  const aehnlichSatz = (k: PrognoseKandidat) => {
    const d = aehnlich.get(k.key);
    return d ? `ähnlicher Titel lief am ${tagMonat(d)}` : null;
  };
  /** „Warum" als Inhalt — in der eigenen Spalte oder, wenn die fehlt (Handy), unter dem Titel (B16). */
  const warumInhalt = (k: PrognoseKandidat) => {
    const ae = aehnlichSatz(k);
    if (k.rang === null)
      return (
        <>
          {k.frei_ab && <b>frei ab {tagMonat(k.frei_ab)}</b>}
          {k.frei_ab && (k.warum.length > 0 || k.gruende.length > 0) && ' · '}
          {k.warum.length ? gruendeMitRichtung(k.warum, 2, bezug(k)).map((g) => g.text).join(' · ') : k.gruende.map((g) => GRUND[g]).join(', ')}
          {ae && ` · ${ae}`}
        </>
      );
    const gruende = gruendeMitRichtung(k.warum, 3, bezug(k));
    if (ae) gruende.push({ richtung: '−', text: ae });
    return (
      <>
        {/* Trenner AUSSERHALB der nicht trennbaren Gründe — nur dort darf die Zeile umbrechen (sonst 572 px breit). */}
        {gruende.map((g, i) => (
          <Fragment key={i}>
            {i > 0 && ' · '}
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {g.richtung && <b>{g.richtung} </b>}
              {g.text}
            </Box>
          </Fragment>
        ))}
      </>
    );
  };
  /** Produkt in der Unterzeile: Kürzel, bei reinen Artikelnummern (2010…) der Name (B29). */
  const produktLabel = (code: string) => (/^\d/.test(code) ? (produktName.get(code) ?? code) : code);
  /** B3 (Persona-Umbau 28.10.): ab Platz 11 ein kompakter Einzeiler — sichtbar, nicht eingeklappt. */
  const kurz = (k: PrognoseKandidat) => k.rang !== null && k.rang > KOMPAKT_AB;
  const einzeilig = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

  // `breit`: Mindestbreiten nur dort, wo die Karte Platz hat (Browser-Abnahme 25.10.: sonst 689/654 px).
  const baue = (schmal: boolean, breit = true, warumUnterTitel = false): Spalte<PrognoseKandidat>[] => [
    { key: 'platz', label: 'Platz', gruppe: 'Wert', align: 'right', render: (k) => k.rang ?? '', sortValue: (k) => (k.rang === null ? null : -k.rang) },
    {
      key: 'titel',
      label: 'Webinar',
      gruppe: 'Wert',
      pflicht: true,
      minWidth: breit ? 220 : undefined,
      render: (k) => {
        const sperrThema = k.gruende.includes('thema_zu_frisch') && k.thema;
        if (kurz(k))
          return (
            // Schmal höchstens 140 px: ein ungebrochener Einzeiler bestimmt sonst die Spaltenbreite (gemessen 28.10.: 73 px Überlauf bei 390 px).
            <Box sx={{ ...einzeilig, maxWidth: { xs: 140, sm: 240, md: 340 }, fontSize: 13 }}>
              {schmal && `${k.rang}. `}
              <Link component={RouterLink} to={`/auswertungen/webinare/${k.last_webinar_id}`} underline="hover" sx={TITEL_SX} title={k.title}>
                {k.title}
              </Link>
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {k.plx_product ? ` · ${produktLabel(k.plx_product)}` : ''}
              </Box>
            </Box>
          );
        return (
          <Box sx={{ lineHeight: 1.3, maxWidth: 340 }}>
            {schmal && k.rang !== null && `${k.rang}. `}
            <Tooltip title={k.thema ? `Thema: ${THEMA_LABEL.get(k.thema) ?? k.thema}${k.thema_quelle === 'ki' ? ' (KI-Vorschlag, unbestätigt)' : ''}` : 'ohne Thema'} arrow placement="top">
              <Link component={RouterLink} to={`/auswertungen/webinare/${k.last_webinar_id}`} underline="hover" sx={TITEL_SX}>
                {k.title}
              </Link>
            </Tooltip>
            {k.klasse === 'test' && <NamensEtikett tip={TERMS.testkandidat.tip}>{TERMS.testkandidat.label}</NamensEtikett>}
            <NichtVorschlagen k={k} onSaved={onSaved} />
            <Unter>
              {k.plx_product ? `${produktLabel(k.plx_product)} · ` : ''}
              {int(k.runs.length)} {k.runs.length === 1 ? 'Termin' : 'Termine'}, zuletzt {datum(k.last_run).replace(/\.20(\d\d)$/, '.$1')}
            </Unter>
            {warumUnterTitel && (
              <Box component="span" sx={{ display: 'block', fontSize: 12, lineHeight: 1.3, mt: 0.25, whiteSpace: 'normal', '& span': { whiteSpace: 'normal !important' } }}>
                {warumInhalt(k)}
              </Box>
            )}
            {sperrThema && <Unter>Thema {THEMA_LABEL.get(k.thema as string) ?? k.thema} lief kürzlich</Unter>}
            {k.anlass && <Chip size="small" label={`Anlass: ${k.anlass.titel}`} sx={{ bgcolor: '#fff6dc', mt: 0.5 }} />}
          </Box>
        );
      },
      sortValue: (k) => k.title,
    },
    {
      key: 'prognose',
      label: TERMS.prognoseTermin.label,
      einheit: TERMS.prognoseTermin.einheit,
      tip: TERMS.prognoseTermin.tip,
      gruppe: 'Wert',
      pflicht: true,
      render: (k) =>
        kurz(k) ? (
          <Box sx={{ whiteSpace: 'nowrap', fontSize: 13 }} title={`mittlere Hälfte ${spanneT(k)}`}>
            ≈ {tw(k.median)}
          </Box>
        ) : (
          <SpannenBalken k={k} max={max} aktiv={k.rang !== null && k.rang <= 3} />
        ),
      sortValue: (k) => k.median,
    },
    {
      key: 'letzte',
      label: TERMS.letzteTermine.label,
      tip: TERMS.letzteTermine.tip,
      gruppe: 'Wert',
      render: (k) => {
        if (kurz(k)) {
          const r0 = k.runs[0];
          const w0 = r0 ? (r0.wert_fest ?? lauf.get(r0.webinar_id)?.wert_summe_fest ?? r0.wert) : null;
          return r0 ? (
            <Box sx={{ whiteSpace: 'nowrap', fontSize: 12, color: r0.reif ? 'inherit' : 'text.disabled' }}>
              {tw(w0)} ({tagMonat(r0.start_date)}){k.runs.length > 1 ? ` · +${k.runs.length - 1}` : ''}
            </Box>
          ) : null;
        }
        const runs = k.runs.slice(0, 3);
        // B31: Termine mit noch offenen Zahlen grau und „offen" — wie in der Nachverfolgung.
        const werte = runs.map((r, i) => {
          const w = r.wert_fest ?? lauf.get(r.webinar_id)?.wert_summe_fest ?? r.wert;
          const offenN = offenBeiTermin(data.zuordnung, r.webinar_id);
          return (
            <Fragment key={r.webinar_id}>
              {i > 0 && ' · '}
              <Box
                component="span"
                sx={{ color: r.reif ? 'inherit' : 'text.disabled' }}
                title={offenN > 0 ? `Zuordnung läuft nach: ${offenN} Bestellungen dieses Termins zählen noch nicht.` : r.reif ? undefined : TERMS.zahlenOffen.tip}
              >
                {tw(w)} ({tagMonat(r.start_date)}){!r.reif && ' · offen'}
                {offenN > 0 && ' · Zuordnung läuft'}
              </Box>
            </Fragment>
          );
        });
        const kontakte = runs.map((r) => r.kontakte ?? (lauf.get(r.webinar_id)?.contacts || null));
        const mit = kontakte.filter((x): x is number => x !== null && x > 0);
        const ohne = kontakte.length - mit.length;
        return (
          <Box sx={{ lineHeight: 1.25, fontSize: 13 }}>
            {werte}
            <Unter>
              {mit.length ? `bei ${mit.map((x) => kompakt(x).replace(' Tsd.', '')).join(' / ')}${mit.every((x) => x < 1_000_000) ? ' Tsd.' : ''} Kontakten` : ''}
              {mit.length && ohne ? ' · ' : ''}
              {ohne ? `${ohne === 1 ? 'einer' : ohne} ohne Kontaktzahl` : ''}
            </Unter>
          </Box>
        );
      },
    },
    {
      key: 'warum',
      label: TERMS.warumKandidat.label,
      einheit: TERMS.warumKandidat.einheit,
      tip: TERMS.warumKandidat.tip,
      gruppe: 'Planung',
      minWidth: breit ? 200 : undefined,
      render: (k) =>
        kurz(k) ? (
          <Box sx={{ ...einzeilig, maxWidth: 280, fontSize: 12 }}>
            {gruendeMitRichtung(k.warum, 1, bezug(k)).map((g) => `${g.richtung ? `${g.richtung} ` : ''}${g.text}`)[0] ?? ''}
          </Box>
        ) : (
          <Box sx={{ fontSize: 13 }}>{warumInhalt(k)}</Box>
        ),
    },
    {
      key: 'pause',
      label: TERMS.pauseWochen.label,
      einheit: TERMS.pauseWochen.einheit,
      tip: TERMS.pauseWochen.tip,
      gruppe: 'Planung',
      align: 'right',
      render: (k) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {wochen(k.pause_konzept)} · {wochen(k.pause_produkt)} · {wochen(k.pause_thema)}
        </Box>
      ),
      sortValue: (k) => k.pause_konzept,
    },
    {
      key: 'quote',
      label: TERMS.bezahlquoteTermine.label,
      tip: TERMS.bezahlquoteTermine.tip,
      gruppe: 'Zahlung (wächst mit dem Alter)',
      align: 'right',
      render: (k) => (k.bezahlquote === null ? 'offen' : formatPct(k.bezahlquote, 0)),
      sortValue: (k) => k.bezahlquote,
    },
    {
      key: 'chance',
      label: TERMS.chanceBesser.label,
      tip: TERMS.chanceBesser.tip,
      gruppe: 'Wert',
      align: 'right',
      render: (k) => {
        if (k.rang === 1) return '';
        const c = erster ? chanceBesser(k, erster) : null;
        return c === null ? '–' : formatPct(c * 100, 0);
      },
      sortValue: (k) => (erster && k.rang !== 1 ? chanceBesser(k, erster) : null),
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(false)), KAND_GRUPPEN);
  const wahl = useSpaltenwahl('kandidaten', meta, KAND_STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(wahl.breite === 'schmal', wahl.breite === 'breit', !sichtbar.has('warum')).filter((c) => sichtbar.has(c.key));
  if (!p) return <Alert severity="warning">Die Rechnung ist gescheitert — die Kandidaten fehlen.</Alert>;
  return (
    <Box ref={wahl.messRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
          Kandidaten für {tagMitWochentag(p.zieltermin)}
        </Typography>
        <SpaltenMenue spalten={meta} wahl={wahl} />
      </Box>
      <SortableTable
        rows={zeilen}
        columns={spalten}
        rowKey={(k) => k.key}
        initialSort={{ key: 'platz', dir: 'desc' }}
        maxHeight={null}
        eng={wahl.breite !== 'breit'}
        abschnitt={(k) => (k.rang === null ? abschnitt : null)}
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Ganz neues Webinar: was je Produkt zu erwarten ist
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[...p.neue_konzepte]
            .sort((a, b) => (b.median ?? 0) - (a.median ?? 0))
            .map((nk) => (
              <Chip
                key={nk.plx_product}
                variant="outlined"
                sx={{ opacity: nk.gruende.length ? 0.7 : 1 }}
                label={`${produktName.get(nk.plx_product) ?? nk.plx_product} ≈ ${tw(nk.median)}${nk.frei_ab ? ` · frei ab ${tagMonat(nk.frei_ab)}` : nk.gruende.length ? ` · ${nk.gruende.map((g) => GRUND[g]).join(', ')}` : ''}`}
              />
            ))}
        </Box>
        <Hinweis>Für ein ganz neues Webinar sagt das Produkt wenig voraus — die Spanne ist ehrlich breit. Termine mit eigener Vorgeschichte treffen besser.</Hinweis>
      </Box>
    </Box>
  );
};

const ThemenBereich: FC<{ data: RankingAntwort; onSaved: () => void }> = ({ data, onSaved }) => {
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Alle Gruppen bleiben editierbar (ein Fehlgriff muss korrigierbar sein); unbestätigte zuerst.
  const alle = [...(data.prognose?.kandidaten ?? [])].sort(
    (a, b) =>
      Number(a.thema_quelle === 'manuell') - Number(b.thema_quelle === 'manuell') ||
      a.title.localeCompare(b.title),
  );
  const ki = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const r = await holeKiThemen();
      setStatus(
        r.ohne_ki
          ? 'KI nicht konfiguriert.'
          : `${r.geschrieben} Vorschläge geschrieben${r.verworfen ? `, ${r.verworfen} verworfen` : ''}.`,
      );
      onSaved();
    } catch (e) {
      setStatus((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" disabled={busy} onClick={ki}>
          KI-Vorschläge für Webinare ohne Thema holen
        </Button>
        {status && <Typography variant="body2">{status}</Typography>}
      </Box>
      {alle.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Keine Webinare in den letzten 12 Monaten.
        </Typography>
      ) : (
        alle.map((k) => (
          <Box
            key={k.key}
            sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 0.75, flexWrap: 'wrap' }}
          >
            <Box sx={{ minWidth: 280, maxWidth: 360 }}>
              <Titel
                title={k.title}
                produkt={k.plx_product}
                zusatz={
                  k.thema_quelle === 'manuell'
                    ? 'bestätigt'
                    : k.thema_quelle === 'ki'
                      ? 'KI-Vorschlag, unbestätigt'
                      : 'ohne Thema'
                }
              />
            </Box>
            <ThemaWahl k={k} onSaved={onSaved} />
          </Box>
        ))
      )}
      <Hinweis>
        Das Thema gilt für alle Läufe eines Webinars (auch mit anderem Produkt) und sperrt dasselbe
        Thema 3 Wochen. „Sonstiges“, „Trading & Cash“ und „Depot & Strategie“ sperren nie — sie
        beschreiben das Format, nicht das Marktthema. Eine Bestätigung von Hand überschreibt die KI
        dauerhaft.
      </Hinweis>
    </Box>
  );
};

const Inhalt: FC<{ data: RankingAntwort; version: number; neuLaden: () => void }> = ({ data, version, neuLaden }) => {
  const [pflegeOffen, setPflegeOffen] = useState(false);
  const [nv, setNv] = useState<NachverfolgungAntwort | null>(null);
  const [nvFehler, setNvFehler] = useState<string | null>(null);
  const [nvNeu, setNvNeu] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetchNachverfolgung()
      .then((d) => !cancelled && setNv(d))
      .catch((e: Error) => !cancelled && setNvFehler(e.message));
    return () => {
      cancelled = true;
    };
  }, [version, nvNeu]);
  const unbestaetigt = (data.prognose?.kandidaten ?? []).filter((k) => k.thema_quelle !== 'manuell').length;
  const zuTun = unbestaetigt > 0;
  const m = data.prognose?.modell;
  const stufe = m?.pausen_stufen?.[0];
  return (
    <>
      <Befund data={data} />
      <Kopf data={data} nv={nv} />
      <KandidatenTabelle data={data} onSaved={neuLaden} />
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
          {TERMS.bilanzEmpfehlung.label}
        </Typography>
        <Nachverfolgung data={nv} fehler={nvFehler} onSaved={() => setNvNeu((x) => x + 1)} zuordnung={data.zuordnung} />
      </Box>
      <Box sx={{ mt: 2 }}>
        <KlappKopf
          offen={pflegeOffen}
          onToggle={() => setPflegeOffen((o) => !o)}
          titel={`${TERMS.vonHandPflegen.label} (wirkt auf die Empfehlung)`}
          tip={TERMS.vonHandPflegen.tip}
          zuTun={zuTun}
          satz={`${int(unbestaetigt)} ${unbestaetigt === 1 ? 'Thema wartet' : 'Themen warten'} auf Bestätigung · ${int(data.sperren.length)} ${data.sperren.length === 1 ? 'Sperre' : 'Sperren'} · ${int(data.anlaesse.length)} ${data.anlaesse.length === 1 ? 'Anlass' : 'Anlässe'}`}
        />
        {pflegeOffen && (
          <Box sx={{ mt: 1.5, display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Themen</Typography>
              <ThemenBereich data={data} onSaved={neuLaden} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Sperren ({data.sperren.length})</Typography>
              <SperrenBereich data={data} onSaved={neuLaden} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Anlässe ({data.anlaesse.length})</Typography>
              <AnlaesseBereich data={data} onSaved={neuLaden} />
            </Box>
          </Box>
        )}
      </Box>
      <Hinweis>
        Gerankt wird die Prognose des Erwarteten Werts eines Termins (netto){m ? `, gelernt an ${int(m.reife_laeufe)} früheren Terminen` : ''}.
        {stufe && ` Ein Webinar, das vor weniger als ${int(stufe.tage)} Tagen lief, wird ${formatPct((1 - stufe.faktor) * 100, 0)} niedriger angesetzt.`} Eine
        Entscheidungshilfe, keine Automatik. {TERMS.wertSumme.label.replace(' (Summe)', '')}: {data.anfo_wert_stand}.
      </Hinweis>
    </>
  );
};

export const EmpfehlungKarte: FC = () => {
  const [data, setData] = useState<RankingAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    // Erster Abruf teilt sich die Antwort mit Webinar- und Produkte-Karte; nach einem Schreibvorgang frisch vom Server.
    (version === 0 ? fetchWebinarRankingGeteilt() : fetchWebinarRanking())
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setFehler(null);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);
  const neuLaden = useCallback(() => setVersion((v) => v + 1), []);
  return (
    <ChartCard
      title="Was als Nächstes laufen sollte"
      howToRead="Der Befund nennt Platz 1 für den nächsten freien Sonntag. Darunter, was angelegt ist und was die Montags-Mail empfahl, dann die Kandidaten mit ihrer Prognose und der mittleren Hälfte als Spanne; ab Platz 11 je eine Zeile. Gesperrte stehen am Ende mit dem Tag, ab dem sie wieder frei sind."
      terms={[
        { label: TERMS.prognoseTermin.label, tip: TERMS.prognoseTermin.tip },
        { label: TERMS.gleichauf.label, tip: TERMS.gleichauf.tip },
        { label: TERMS.testkandidat.label, tip: TERMS.testkandidat.tip },
        { label: TERMS.letzteTermine.label, tip: TERMS.letzteTermine.tip },
      ]}
      source="Webinare der letzten 12 Monate, PL-X-Bestellungen, Anfo-Wert je Anfo, Themen/Sperren/Anlässe von Hand"
      freshnessKey={['webinar_send_metrics', 'plx_orders', 'webinar_zuordnung']}
    >
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          {fehler && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Neu laden fehlgeschlagen: {fehler} — angezeigt ist der Stand davor.
            </Alert>
          )}
          <Inhalt data={data} version={version} neuLaden={neuLaden} />
        </>
      )}
    </ChartCard>
  );
};
